import React, { useState, useEffect } from "react";
import {
  fetchPracticalInfo,
  defaultPracticalInfo,
  type PracticalInfo as PracticalInfoType,
} from "../../../services/practicalInfoService";
import "../programacion.css";

const FOCUS_RING =
  "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-white";

const LINK_CLASS = `underline underline-offset-2 hover:text-[#ff5b52] transition-colors duration-200 ${FOCUS_RING}`;

const DASHED_BOX =
  "p-4 md:p-6 rounded-2xl outline outline-1 outline-offset-[-1px] outline-white/40 [outline-style:dashed]";

const SECTION_TITLE =
  "text-Colors-white text-2xl md:text-4xl font-bold leading-tight mb-3";

const MUTED_TEXT = "text-[#b9b9cf] text-base md:text-lg";

const BODY_TEXT = "text-Colors-white text-base md:text-xl leading-relaxed";

const ExternalLink: React.FC<{ href: string; children: React.ReactNode }> = ({
  href,
  children,
}) => (
  <a
    href={href}
    target="_blank"
    rel="noopener noreferrer"
    className={LINK_CLASS}
  >
    {children}
    <span className="sr-only"> (se abre en una pestaña nueva)</span>
  </a>
);

const VenueMap: React.FC<{
  title: string;
  src: string;
  href: string;
  name: string;
}> = ({ title, src, href, name }) => (
  <figure className="m-0">
    <iframe
      title={title}
      src={src}
      className="w-full h-56 md:h-72 rounded-2xl border-0"
      loading="lazy"
      referrerPolicy="strict-origin-when-cross-origin"
      allowFullScreen
    />
    <figcaption className={`mt-3 ${BODY_TEXT}`}>
      <ExternalLink href={href}>
        Abrir mapa de {name} en Google Maps
      </ExternalLink>
    </figcaption>
  </figure>
);

const PracticalInfo: React.FC = () => {
  const [info, setInfo] = useState<PracticalInfoType>(defaultPracticalInfo);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const loadInfo = async () => {
      setIsLoading(true);
      const data = await fetchPracticalInfo();
      setInfo(data);
      setIsLoading(false);
    };

    loadInfo();
  }, []);

  return (
    <div className="relative w-full">
      <div className="c-decor-hex c-decor-hex--header" aria-hidden="true" />

      <div className="p-programa__container">
        <header className="mb-8 md:mb-10">
          <h1 className="w-full text-Colors-white font-semibold text-4xl md:text-5xl leading-tight mb-4">
            Información práctica
          </h1>
          <p className={`${BODY_TEXT} font-normal`}>
            Lo que necesitas el día del evento: cómo llegar, red, hashtag y cómo
            encontrar al equipo.
          </p>
        </header>

        {isLoading && (
          <p role="status" aria-live="polite" className={`${MUTED_TEXT} mb-4`}>
            Actualizando información...
          </p>
        )}

        <div className="flex flex-col gap-8 md:gap-10">
          <section aria-labelledby="arrive-heading">
            <h2 id="arrive-heading" className={SECTION_TITLE}>
              Cómo llegar
            </h2>
            <div className={DASHED_BOX}>
              <p className={`${MUTED_TEXT} mb-3`}>{info.location.name}</p>
              <p className={`${BODY_TEXT} mb-3`}>
                Puedes venir en tu propio vehículo o en{" "}
                <ExternalLink href="https://tuvillavesa.es/es/lines-and-routes">
                  la línea 16 de autobús urbano
                </ExternalLink>{" "}
                (parada Noáin c/ Real, frente nº 22).
              </p>
              <p className={`${BODY_TEXT} mb-3`}>
                Si vienes en coche, una buena alternativa es el aparcamiento en
                Noáin. También tienes{" "}
                <ExternalLink href={info.location.parkingLink}>
                  un aparcamiento alternativo en Google Maps
                </ExternalLink>
                .
              </p>
              <VenueMap
                title={`Mapa de ${info.location.name}`}
                src={info.location.mapEmbed}
                href={info.location.mapLink}
                name={info.location.name}
              />
            </div>
          </section>

          <section aria-labelledby="wifi-heading">
            <h2 id="wifi-heading" className={SECTION_TITLE}>
              WiFi
            </h2>
            <div className={DASHED_BOX}>
              <dl className="grid grid-cols-1 gap-2">
                <div className="bg-white text-Colors-blue-dark rounded-[2px] px-4 py-3 min-h-[72px] flex flex-col justify-center gap-0.5">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-[#b42318]">
                    Red
                  </dt>
                  <dd className="text-xl md:text-2xl font-extrabold leading-snug break-words">
                    {info.wifi.network}
                  </dd>
                </div>
                <div className="bg-white text-Colors-blue-dark rounded-[2px] px-4 py-3 min-h-[72px] flex flex-col justify-center gap-0.5">
                  <dt className="text-xs font-semibold uppercase tracking-wide text-[#b42318]">
                    Contraseña
                  </dt>
                  <dd className="text-xl md:text-2xl font-extrabold leading-snug break-words">
                    {info.wifi.password}
                  </dd>
                </div>
              </dl>
            </div>
          </section>

          <section aria-labelledby="social-heading">
            <h2 id="social-heading" className={SECTION_TITLE}>
              Comparte tu experiencia
            </h2>
            <div className={DASHED_BOX}>
              <p className={`${MUTED_TEXT} mb-2`}>
                Utiliza nuestro hashtag oficial
              </p>
              <p className="text-white text-[1.05rem] font-extrabold uppercase tracking-[1.5px] break-words">
                {info.socialMedia.hashtag}
              </p>
            </div>
          </section>

          <section aria-labelledby="allies-heading">
            <h2 id="allies-heading" className={SECTION_TITLE}>
              Localiza a tus aliadas y aliados
            </h2>
            <figure
              className={`m-0 flex flex-col sm:flex-row sm:items-start gap-4 ${DASHED_BOX}`}
            >
              <img
                src="/images/credencial-organizacion.png"
                alt="Ejemplo de credencial azul de organización del Open Space by JAKALA"
                className="w-28 md:w-36 h-auto shrink-0 rounded-sm"
              />
              <figcaption className="flex flex-col gap-2">
                <p className={MUTED_TEXT}>No te pierdas entre tanta gente</p>
                <p className={BODY_TEXT}>
                  Busca, de entre todos los asistentes, los que tienen la
                  identificación con esta acreditación.
                </p>
              </figcaption>
            </figure>
          </section>

          <section
            aria-labelledby="post-event-heading"
            className="mb-12 md:mb-16"
          >
            <h2 id="post-event-heading" className={SECTION_TITLE}>
              Post-evento
            </h2>
            <div className={DASHED_BOX}>
              <p className={`${MUTED_TEXT} mb-3`}>
                Para cerrar la jornada por lo alto, te esperamos en:
              </p>
              <p className={`${BODY_TEXT} mb-1`}>{info.postEvent.name}</p>
              <p className={`${BODY_TEXT} mb-3`}>
                A partir de las{" "}
                <time dateTime={info.postEvent.time}>{info.postEvent.time}</time>{" "}
                h
              </p>
              <VenueMap
                title={`Mapa de ${info.postEvent.name}`}
                src={info.postEvent.mapEmbed}
                href={info.postEvent.mapLink}
                name={info.postEvent.name}
              />
            </div>
          </section>
        </div>
      </div>
    </div>
  );
};

export default PracticalInfo;
